import { z } from 'zod';
export const ROLES = ['ADMIN', 'MANAGER', 'OFFICE', 'MECHANIC'];
export const Role = z.enum(ROLES);
export const WO_STATUSES = [
    'OPEN',
    'ASSIGNED',
    'IN_PROGRESS',
    'ON_HOLD',
    'READY_FOR_REVIEW',
    'CLOSED',
    'VOIDED',
];
export const WoStatus = z.enum(WO_STATUSES);
/** Shop-friendly labels used in the UI. */
export const STATUS_LABELS = {
    OPEN: 'Open',
    ASSIGNED: 'Assigned',
    IN_PROGRESS: 'In Progress',
    ON_HOLD: 'On Hold',
    READY_FOR_REVIEW: 'Ready for Review',
    CLOSED: 'Closed',
    VOIDED: 'Voided',
};
export const ROLE_LABELS = {
    ADMIN: 'Admin',
    MANAGER: 'Manager',
    OFFICE: 'Office',
    MECHANIC: 'Mechanic',
};
/** Statuses that sit in a mechanic's column on the Shop Board. */
export const BOARD_STATUSES = ['ASSIGNED', 'IN_PROGRESS', 'ON_HOLD', 'READY_FOR_REVIEW'];
export const VOID_REASON_MIN = 15;
export const REASON_MIN = 3;
// ---------- request bodies ----------
const optText = (max) => z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((v) => (v ? v : null));
const reqText = (max) => z.string().trim().min(1).max(max);
const id = z.coerce.number().int().positive();
export const CustomerInput = z.object({
    name: reqText(200),
    phone: optText(40),
    email: z.string().trim().email().max(254).optional().nullable().or(z.literal('')).transform((v) => v || null),
    billingAddress: optText(2000),
    notes: optText(5000),
    confirmDuplicate: z.boolean().optional(),
});
export const CustomerPatch = CustomerInput.omit({ confirmDuplicate: true })
    .partial()
    .extend({ active: z.boolean().optional() });
export const ContactInput = z.object({
    name: reqText(120),
    phone: optText(40),
    email: z.string().trim().email().max(254).optional().nullable().or(z.literal('')).transform((v) => v || null),
    receivesEmail: z.boolean().default(false),
});
export const ContactPatch = ContactInput.partial().extend({ active: z.boolean().optional() });
export const MachineInput = z.object({
    customerId: id.optional().nullable(),
    make: reqText(80),
    model: reqText(80),
    serialNumber: reqText(80),
    equipmentNo: optText(80),
    controls: optText(20),
    isRentalFleet: z.boolean().default(false),
});
export const MachinePatch = MachineInput.partial().extend({ active: z.boolean().optional() });
export const WorkOrderCreate = z.object({
    customerId: id,
    machineId: id,
    problemDescription: reqText(10000),
    jobType: reqText(60),
    billingType: optText(20),
    customerPo: optText(60),
    machineLocation: optText(200),
    assignToUserId: id.optional().nullable(),
});
export const WorkOrderPatch = z
    .object({
    problemDescription: reqText(10000),
    jobType: reqText(60),
    billingType: optText(20),
    customerPo: optText(60),
    machineLocation: optText(200),
})
    .partial();
export const ACTIONS = [
    'assign',
    'reassign',
    'hold',
    'resume',
    'ready',
    'sendBack',
    'close',
    'reopen',
];
export const Action = z.enum(ACTIONS);
export const TransitionInput = z.object({
    action: Action,
    mechanicId: id.optional(),
    reason: z.string().trim().max(5000).optional(),
    holdReason: z.string().trim().max(60).optional(),
    position: z.number().int().min(0).optional(),
    /** Hold by a mechanic who is clocked in on the job: the Work Performed note for that punch. */
    workNote: z.string().trim().max(10_000).optional(),
});
export const VoidRequestInput = z.object({ reason: z.string().trim().min(VOID_REASON_MIN).max(5000) });
export const VoidDecisionInput = z.object({
    approve: z.boolean(),
    note: z.string().trim().max(5000).optional(),
});
export const ReorderInput = z.object({
    mechanicId: id,
    workOrderIds: z.array(id).max(500),
});
export const UserInput = z.object({
    email: z.string().trim().toLowerCase().email().max(254).optional().nullable().or(z.literal('')).transform((v) => v || null),
    name: reqText(120),
    /** A built-in role (ADMIN, MANAGER, OFFICE, MECHANIC) or the key of a custom role in settings.roles. */
    role: z.string().regex(/^[A-Z][A-Z0-9_]{1,39}$/),
    /** Hourly: shows the office Clock In / Clock Out (mechanics always clock). */
    tracksHours: z.boolean().optional(),
});
export const UserPatch = UserInput.partial().extend({ active: z.boolean().optional() });
/** Choices offered for a new job (or a changed one): everything not retired, plus its current value. */
export function openChoices(all, retired, current) {
    return all.filter((x) => !retired.includes(x) || x === current);
}
/** Parts List: the office ticks a line when the part has arrived (change request 2026-11). */
export const PartArrivedInput = z.object({ arrived: z.boolean() });
//# sourceMappingURL=domain.js.map